import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import type { EnlaceSinUsar } from '@actual-app/core/server/encryption/cofre-app';

import { useAuth } from '#auth/AuthProvider';
import { Permissions } from '#auth/types';
import { closeBudget, loadAllFiles } from '#budgetfiles/budgetfilesSlice';
import { Boton } from '#components/mobile/ui/Boton';
import { Cargando } from '#components/mobile/ui/Cargando';
import { ConfirmarHoja } from '#components/mobile/ui/ConfirmarHoja';
import { EstadoVacio } from '#components/mobile/ui/EstadoVacio';
import { Hoja } from '#components/mobile/ui/Hoja';
import { Icono } from '#components/mobile/ui/Icono';
import { color, radio, texto } from '#components/mobile/ui/tokens';
import { useMetadataPref } from '#hooks/useMetadataPref';
import { useDispatch, useSelector } from '#redux';

import { AyudaPermisos } from './AyudaPermisos';
import { EnlaceSinUsarFila } from './EnlaceSinUsarFila';
import { EtiquetaPermiso } from './EtiquetaPermiso';
import type { Permiso } from './EtiquetaPermiso';
import { FilaTripulante } from './FilaTripulante';
import { InicialAutor } from './InicialAutor';
import { LineaGavi } from './LineaGavi';
import { MarcoPantalla } from './MarcoPantalla';
import { PasoInvitar } from './PasoInvitar';
import { RotuloLista } from './RotuloLista';
import { tarjetaPlana } from './tarjetaPlana';
import { tripulacion } from './tripulacionLogica';
import type { Tripulante } from './tripulacionLogica';
import { useAvisar } from './useAvisar';

type PantallaTripulacionProps = {
  /** El barco en la nube (`cloudFileId`). */
  fileId: string;
  nombre: string;
  onCerrar: () => void;
};

/**
 * «Tripulación» de un barco, a pantalla completa (puerto, Ajustes e Inicio).
 * Quien lleva el mando ve a cada persona en una línea, con «…» para cambiar
 * su permiso o bajarla del barco, los enlaces sin usar e «Invitar a alguien»
 * (un paso aparte). Quien va a bordo ve la tripulación y puede bajarse.
 */
export function PantallaTripulacion({
  fileId,
  nombre,
  onCerrar,
}: PantallaTripulacionProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const avisar = useAvisar();
  const { hasPermission } = useAuth();
  const miId = useSelector(state => state.user.data?.userId ?? null);
  const allFiles = useSelector(state => state.budgetfiles.allFiles ?? []);
  const [abierto] = useMetadataPref('cloudFileId');
  const [idLocal] = useMetadataPref('id');
  const [paso, setPaso] = useState<'lista' | 'invitar'>('lista');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState(false);
  const [confirmar, setConfirmar] = useState<Tripulante | 'yo' | null>(null);
  const [enlaces, setEnlaces] = useState<EnlaceSinUsar[]>([]);

  const archivo = allFiles.find(
    f => 'cloudFileId' in f && f.cloudFileId === fileId,
  );
  const accesos =
    archivo && 'usersWithAccess' in archivo ? archivo.usersWithAccess : [];
  const duenoId = archivo && 'owner' in archivo ? archivo.owner : null;
  const keyId =
    (archivo && 'encryptKeyId' in archivo && archivo.encryptKeyId) || null;
  const esDueno = miId !== null && duenoId === miId;
  // La administración puede cambiar permisos y bajar a gente; invitar y los
  // enlaces son solo de quien es dueño (así lo comprueba el servidor).
  const alMando = esDueno || hasPermission(Permissions.ADMINISTRATOR);

  const tripulantes = tripulacion(accesos, miId);
  const capitan = tripulantes.find(p => p.userId === duenoId);
  // Al mando, luego tú (si vas a bordo) y luego el resto.
  const resto = tripulantes
    .filter(p => p.userId !== duenoId)
    .sort((a, b) => Number(b.esYo) - Number(a.esYo));
  const datoDe = (userId: string) => accesos.find(a => a.userId === userId);
  const permisoDe = (userId: string): Permiso =>
    datoDe(userId)?.permiso === 'ver' ? 'ver' : 'editar';
  const nombreFila = (p: Tripulante) =>
    p.esYo ? t('You · {{name}}', { name: p.nombre }) : p.nombre;

  const cargarEnlaces = async () => {
    const res = await send('pareja-enlaces-sin-usar', { fileId });
    // Sin conexión se deja la lista como estaba, en vez de vaciarla.
    if (res.enlaces) {
      setEnlaces(res.enlaces);
    }
  };

  useEffect(() => {
    if (esDueno && paso === 'lista') {
      void cargarEnlaces();
    }
    // Al abrir y al volver de «Invitar a alguien».
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId, esDueno, paso]);

  /** Pide el cambio al servidor, avisa y recarga la lista de barcos. */
  const guardar = async (pedir: () => Promise<boolean>, aviso: string) => {
    setOcupado(true);
    try {
      const fallo = await pedir();
      setError(fallo);
      if (!fallo) {
        avisar(aviso);
      }
      // El barco del puerto («Con …») y esta lista leen la lista de barcos.
      await dispatch(loadAllFiles());
    } catch {
      setError(true);
    } finally {
      setOcupado(false);
    }
  };

  const cambiarPermiso = (p: Tripulante) => {
    const nuevo: Permiso = permisoDe(p.userId) === 'ver' ? 'editar' : 'ver';
    return guardar(
      async () => {
        const res = await send('access-set-permiso', {
          fileId,
          userId: p.userId,
          permiso: nuevo,
        });
        return Boolean(res?.error);
      },
      nuevo === 'ver'
        ? t('{{name}} now only views', { name: p.nombre })
        : t('{{name}} now views and edits', { name: p.nombre }),
    );
  };

  const bajar = (p: Tripulante) => {
    setConfirmar(null);
    return guardar(
      async () => {
        const res = await send('access-delete-all', {
          fileId,
          ids: [p.userId],
        });
        return 'error' in res || res.someDeletionsFailed;
      },
      t('{{name}} is no longer aboard', { name: p.nombre }),
    );
  };

  const anular = async (enlace: EnlaceSinUsar) => {
    const res = await send('pareja-anular-enlace', { fileId, id: enlace.id });
    if (res.error) {
      avisar(t('Could not do it right now. Try again later.'));
      return;
    }
    setEnlaces(lista => lista.filter(e => e.id !== enlace.id));
    avisar(t('Link cancelled'));
  };

  // Solo con el barco abierto: se baja de él y se quita de este móvil.
  const puedeBajarme =
    !alMando && tripulantes.some(p => p.esYo) && abierto === fileId;
  const bajarme = async () => {
    setConfirmar(null);
    const res = await send('pareja-bajarme');
    if (res.error || !idLocal) {
      setError(true);
      return;
    }
    onCerrar();
    await dispatch(closeBudget());
    await send('delete-budget', { id: idLocal });
    await dispatch(loadAllFiles());
    avisar(
      t('You got off «{{name}}». It is no longer on this phone.', {
        name: nombre,
      }),
    );
  };

  // La lista de barcos aún no ha llegado: ni la vista de quien va a bordo
  // ni invitar sin la llave del barco.
  if (!archivo) {
    return (
      <MarcoPantalla titulo={t('Crew')} subtitulo={nombre} onAtras={onCerrar}>
        <Cargando />
      </MarcoPantalla>
    );
  }

  if (paso === 'invitar') {
    return (
      <PasoInvitar
        fileId={fileId}
        nombre={nombre}
        keyId={keyId}
        onVolver={() => setPaso('lista')}
      />
    );
  }

  const persona = confirmar !== 'yo' ? confirmar : null;

  let pie: ReactNode;
  if (esDueno) {
    pie = (
      <Boton
        bloque
        data-testid="invitar-a-alguien"
        onPress={() => setPaso('invitar')}
      >
        <Icono nombre="plus" size={20} strokeWidth={2.6} />
        <Trans>Invite someone</Trans>
      </Boton>
    );
  } else if (puedeBajarme) {
    pie = (
      <Boton
        variante="tonal"
        bloque
        data-testid="bajarme-del-barco"
        onPress={() => setConfirmar('yo')}
      >
        <Trans>Get off the boat</Trans>
      </Boton>
    );
  }

  let fraseGavi: ReactNode;
  if (alMando) {
    fraseGavi = (
      <Trans>
        <strong>You decide here</strong> who comes aboard.
      </Trans>
    );
  } else if (capitan) {
    fraseGavi = (
      <Trans
        i18nKey="<strong>This boat belongs to {{name}}.</strong> You are aboard."
        values={{ name: capitan.nombre }}
        components={{ strong: <strong /> }}
      />
    );
  } else {
    fraseGavi = (
      <Trans>
        <strong>You are aboard.</strong> Whoever is in command decides who comes
        aboard.
      </Trans>
    );
  }

  return (
    <MarcoPantalla
      titulo={alMando ? t('Crew') : t('You are aboard')}
      subtitulo={nombre}
      onAtras={onCerrar}
      pie={pie}
    >
      <LineaGavi>{fraseGavi}</LineaGavi>
      <RotuloLista>
        {t('Aboard · {{n}}', { n: tripulantes.length })}
      </RotuloLista>
      <View style={estiloLista}>
        {capitan && (
          <FilaTripulante
            tripulante={capitan}
            nombre={nombreFila(capitan)}
            tipo="mando"
          />
        )}
        {resto.map(p => (
          <FilaTripulante
            key={p.userId}
            tripulante={p}
            nombre={nombreFila(p)}
            tipo={permisoDe(p.userId)}
            menu={
              alMando && !p.esYo
                ? {
                    ocupado,
                    onCambiarPermiso: () => void cambiarPermiso(p),
                    onBajar: () => setConfirmar(p),
                  }
                : undefined
            }
          />
        ))}
      </View>
      {esDueno && enlaces.length > 0 && (
        <>
          <RotuloLista>
            <Trans>Waiting to come aboard</Trans>
          </RotuloLista>
          <View style={estiloLista}>
            {enlaces.map(enlace => (
              <EnlaceSinUsarFila
                key={enlace.id}
                enlace={enlace}
                onAnular={() => void anular(enlace)}
              />
            ))}
          </View>
        </>
      )}
      {esDueno && resto.length === 0 && enlaces.length === 0 && (
        <EstadoVacio
          data-testid="tripulacion-vacia"
          ilustracion="barquito"
          anchoIlustracion={96}
          titulo={t('Right now there is nobody else aboard')}
          texto={t('Invite whoever you like: partner, family or friends.')}
        />
      )}
      <AyudaPermisos />
      {error && (
        <Text style={{ ...texto.secundario, color: color.bad }}>
          <Trans>Could not save the change. Try again later.</Trans>
        </Text>
      )}
      {persona && (
        <Hoja
          aria-label={t('Take {{name}} off the boat?', {
            name: persona.nombre,
          })}
          onCerrar={() => setConfirmar(null)}
        >
          <ConfirmarHoja
            icono="users"
            pregunta={t('Take {{name}} off the boat?', {
              name: persona.nombre,
            })}
            detalle={t(
              'They will stop seeing this budget on their phone. What they added stays in the plan. If you change your mind, send them another link.',
            )}
            textoAccion={t('Take off the boat')}
            testIdAccion="confirmar-bajar"
            onConfirmar={() => void bajar(persona)}
            onCancelar={() => setConfirmar(null)}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                borderRadius: radio.boton,
                backgroundColor: color.surface,
              }}
            >
              <InicialAutor tripulante={persona} size={40} />
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={{ fontSize: 15, fontWeight: 800 }}>
                  {persona.nombre}
                </Text>
                <Text
                  data-testid="confirmar-correo"
                  style={{
                    fontSize: 12.5,
                    color: color.fg2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {datoDe(persona.userId)?.userName}
                </Text>
              </View>
              <EtiquetaPermiso tipo={permisoDe(persona.userId)} />
            </View>
          </ConfirmarHoja>
        </Hoja>
      )}
      {confirmar === 'yo' && (
        <Hoja
          aria-label={t('Get off this budget?')}
          onCerrar={() => setConfirmar(null)}
        >
          <ConfirmarHoja
            icono="users"
            pregunta={t('Get off this budget?')}
            detalle={t(
              'You stop seeing it and it is removed from this phone. What you added stays in the plan. To come back, ask for another link.',
            )}
            textoAccion={t('Get off the boat')}
            testIdAccion="confirmar-bajarme"
            onConfirmar={() => void bajarme()}
            onCancelar={() => setConfirmar(null)}
          />
        </Hoja>
      )}
    </MarcoPantalla>
  );
}

const estiloLista = {
  ...tarjetaPlana,
  // Con los atributos gana al estilo base de cada fila (que quita el borde).
  '& > [data-testid] + [data-testid]': {
    borderTop: `1px solid ${color.line}`,
  },
} as const;
